import { Injectable } from '@angular/core';
import { isNullish } from '@commonHelpers/math-utils';

/**
 * Fehlertexte, die auftreten, wenn ein Lazy-Chunk des laufenden Releases nicht mehr
 * geladen werden kann. Ursache ist praktisch immer eine veraltete index.html im
 * Browser-Cache, die auf Bundles eines bereits abgeloesten Releases verweist.
 */
const chunkErrorMessageParts = [
	'ChunkLoadError',
	'Loading chunk',
	'Loading CSS chunk',
	'Failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'Importing a module script failed',
	// Der Browser hat HTML statt JavaScript erhalten und die Auswertung schon wegen des
	// MIME-Typs verweigert - eindeutig ein Bundle-Problem, kein Anwendungsfehler.
	'Failed to load module script'
];

/*
 * Bewusst NICHT erkannt werden Syntaxfehler wie "SyntaxError: Unexpected token '<'".
 * Sie entstehen zwar auch, wenn ein fehlendes Bundle als index.html ausgeliefert wird -
 * aber genauso, wenn ein API-Endpunkt statt JSON eine HTML-Fehler- oder Loginseite
 * liefert. Der Error traegt keine verlaessliche Information darueber, welcher der beiden
 * Faelle vorliegt: Der Stack verweist im Produktionsbuild auch bei reinen API-Fehlern auf
 * Bundle-Dateien. Eine Fehlerkennung waere hier teuer, weil sie saemtliche Caches leert
 * und eine Recovery-Navigation ausloest.
 */

const attemptStorageKey = 'chunkRecoveryAttempt';
const recoveredBuildStorageKey = 'chunkRecoveryBuild';
const cacheBusterParam = 'cacheBuster';
const maxAttempts = 2;
const fetchTimeoutMs = 4000;

/**
 * Holt eine Anwendung aus dem Zustand "weisser Bildschirm nach Release" zurueck.
 *
 * Ein einfaches location.reload() genuegt dafuer nicht: Es umgeht den HTTP-Cache
 * des Browsers nicht, der Browser liefert also erneut dieselbe veraltete
 * index.html aus und der Fehler wiederholt sich. Deshalb wird hier eskalierend
 * vorgegangen und die Zahl der Versuche begrenzt, damit keine Reload-Schleife
 * entsteht.
 */
@Injectable({
	providedIn: 'root'
})
export class ChunkRecoveryService {
	private recoveryInProgress = false;

	/** Prueft, ob der Fehler auf einen nicht mehr ladbaren Chunk zurueckgeht. */
	public isChunkLoadError(error?: Error): boolean {
		if (isNullish(error) || isNullish(error.message)) {
			return false;
		}
		return chunkErrorMessageParts.some(messagePart => error.message.includes(messagePart));
	}

	/**
	 * Laedt die Seite unter Umgehung des HTTP-Caches neu.
	 *
	 * currentBuild ist die Release-Kennung des gerade laufenden Standes
	 * (globalEnvironment.buildDate). Sie wird mitgeschrieben, damit beim naechsten
	 * Start erkennbar ist, ob der Reload ueberhaupt ein neues Release gebracht hat.
	 *
	 * Rueckgabe false bedeutet: Es wurde kein weiterer Versuch mehr unternommen,
	 * der Aufrufer soll den Fehler regulaer behandeln bzw. protokollieren.
	 */
	public async recover(currentBuild?: string): Promise<boolean> {
		if (this.recoveryInProgress) {
			return true;
		}

		const attempt = this.getAttempts() + 1;
		if (attempt > maxAttempts) {
			return false;
		}

		this.recoveryInProgress = true;
		this.setAttempts(attempt);
		this.setRecoveredBuild(currentBuild);

		if (attempt === 1) {
			await this.revalidateDocument();
			this.reloadWindow();
			return true;
		}

		// Zweiter Versuch: Eine abweichende URL kann den alten Cache-Eintrag gar nicht
		// erst treffen. Das greift auch dann, wenn der Browser den per fetch
		// aufgefrischten Eintrag nicht fuer die Navigation wiederverwendet.
		const url = new URL(this.getCurrentHref());
		url.searchParams.set(cacheBusterParam, Date.now().toString());
		this.replaceWindowLocation(url.toString());
		return true;
	}

	/**
	 * Nach dem Start der Anwendung aufzurufen.
	 *
	 * Der Versuchszaehler wird nur dann zurueckgesetzt, wenn seit der letzten
	 * Rettung tatsaechlich ein anderes Release geladen wurde. Wuerde bei jedem
	 * Start zurueckgesetzt, liefe die Anwendung endlos im Kreis: Die veraltete
	 * index.html startet ja erfolgreich, erst die Navigation in einen Lazy-Chunk
	 * scheitert.
	 */
	public resetAfterSuccessfulBoot(currentBuild?: string): void {
		if (this.getAttempts() > 0 && this.getRecoveredBuild() === currentBuild) {
			return;
		}
		this.setAttempts(0);
		this.setRecoveredBuild(undefined);
		this.removeCacheBusterFromUrl();
	}

	/**
	 * cache: 'reload' erzwingt die Revalidierung und aktualisiert dabei den HTTP-Cache-Eintrag
	 * des Dokuments - genau das, was ein reines location.reload() nicht leistet.
	 *
	 * Die Wartezeit ist begrenzt: Antwortet der Server gar nicht, wuerde der Aufrufer den
	 * Reload sonst nie erreichen und der Nutzer bliebe endgueltig auf dem weissen Bildschirm.
	 * Der AbortController bricht die Anfrage ab, das Rennen gegen den Zeitgeber stellt
	 * zusaetzlich sicher, dass es auch dann weitergeht, wenn der Abbruch nicht greift.
	 */
	private async revalidateDocument(): Promise<void> {
		const abortController = new AbortController();
		let expire: () => void = () => { };
		const expired = new Promise<void>(resolve => { expire = resolve; });
		const timeoutId = window.setTimeout(() => {
			abortController.abort();
			expire();
		}, fetchTimeoutMs);

		// Offline, abgebrochen oder Anfrage blockiert - der Reload wird trotzdem versucht.
		const request = fetch(this.getCurrentHref(), {
			cache: 'reload',
			credentials: 'same-origin',
			signal: abortController.signal
		}).catch(() => undefined);

		try {
			await Promise.race([request, expired]);
		} finally {
			window.clearTimeout(timeoutId);
		}
	}

	protected getCurrentHref(): string {
		return window.location.href;
	}

	protected reloadWindow(): void {
		window.location.reload();
	}

	protected replaceWindowLocation(url: string): void {
		window.location.replace(url);
	}

	protected replaceHistoryState(url: string): void {
		window.history.replaceState(null, '', url);
	}

	private removeCacheBusterFromUrl(): void {
		const url = new URL(this.getCurrentHref());
		if (!url.searchParams.has(cacheBusterParam)) {
			return;
		}
		url.searchParams.delete(cacheBusterParam);
		this.replaceHistoryState(url.pathname + url.search + url.hash);
	}

	private getAttempts(): number {
		return Number(this.readStorage(attemptStorageKey)) || 0;
	}

	private setAttempts(attempt: number): void {
		this.writeStorage(attemptStorageKey, attempt === 0 ? undefined : attempt.toString());
	}

	private getRecoveredBuild(): string | undefined {
		return this.readStorage(recoveredBuildStorageKey);
	}

	private setRecoveredBuild(currentBuild?: string): void {
		this.writeStorage(recoveredBuildStorageKey, currentBuild);
	}

	// Firefox wirft beim Zugriff auf sessionStorage, wenn Cookies beim Schliessen
	// geloescht werden - der Zugriff ist deshalb durchgaengig abgesichert.
	private readStorage(key: string): string | undefined {
		try {
			return window.sessionStorage.getItem(key) ?? undefined;
		} catch {
			return undefined;
		}
	}

	private writeStorage(key: string, value?: string): void {
		try {
			if (isNullish(value)) {
				window.sessionStorage.removeItem(key);
				return;
			}
			window.sessionStorage.setItem(key, value);
		} catch {
			// siehe readStorage
		}
	}
}
